// Mock data for Pazzutti's portfolio

import { formatSemester } from '../lib/dateUtils';

// Marco zero do curso de Engenharia de Software na PUC Minas.
// Única fonte de verdade para "anos de experiência" e "período atual" —
// tudo o que depende de tempo é derivado daqui em tempo de execução.
const ACADEMIC_START_DATE = '2024-02-01';

export const personalInfo = {
  name: 'Pazzutti',
  startDate: ACADEMIC_START_DATE,
  title: {
    pt: 'Desenvolvedor de Software',
    en: 'Software Developer'
  },
  subtitle: {
    pt: 'Estudante de Engenharia de Software na PUC Minas',
    en: 'Software Engineering Student at PUC Minas'
  },
  location: {
    pt: 'Belo Horizonte, MG',
    en: 'Belo Horizonte, MG'
  },
  avatar: 'https://github.com/pazzutti.png',
  bio: {
    pt: `Sou estudante de Engenharia de Software na PUC Minas (${formatSemester(ACADEMIC_START_DATE, 'pt')}) com experiência como estagiário de desenvolvimento na Atalaia/Fictor Alimentos. Sou apaixonado por programação prática, gosto de colocar a mão na massa e já participei de projetos interdisciplinares voltados para o mercado. Tenho grande interesse em desenvolvimento full-stack e sempre busco aprender novas tecnologias.`,
    en: `I am a Software Engineering student at PUC Minas (${formatSemester(ACADEMIC_START_DATE, 'en')}) with experience as a development intern at Atalaia/Fictor Alimentos. I am passionate about practical programming, I like to get my hands dirty and have participated in interdisciplinary projects aimed at the market. I have great interest in full-stack development and always seek to learn new technologies.`
  },
  contact: {
    email: 'arthur.pazzutti0@gmail.com',
    linkedin: 'https://www.linkedin.com/in/arthur-pazzutti-635ba4330/',
    github: 'https://github.com/pazzutti',
    instagram: 'https://instagram.com/arthur.pazzutti'
  }
};

// `level` (0-100) alimenta as barras de progresso da seção Skills.
// Os valores refletem o tempo de uso real de cada tecnologia: JavaScript e Git
// acompanham o curso desde o início, React/NestJS/Azure vêm do estágio na
// Atalaia, Java é majoritariamente acadêmico e Azure é o contato mais recente.
export const skills = {
  technical: [
    { name: 'JavaScript', icon: '🟨', level: 80 },
    { name: 'TypeScript', icon: '🔵', level: 65 },
    { name: 'Java', icon: '☕', level: 70 },
    { name: 'React', icon: '⚛️', level: 80 },
    { name: 'NestJS', icon: '🚀', level: 60 },
    { name: 'Git', icon: '📚', level: 85 },
    { name: 'GitHub', icon: '🐱', level: 85 },
    { name: 'Azure', icon: '☁️', level: 45 }
  ],
  // `rating` (1-5) define quantas estrelas acendem — valor fixo, não aleatório.
  soft: [
    { pt: 'Proatividade', en: 'Proactivity', rating: 5 },
    { pt: 'Trabalho em Equipe', en: 'Teamwork', rating: 5 },
    { pt: 'Comunicação', en: 'Communication', rating: 4 },
    { pt: 'Resolução de Problemas', en: 'Problem Solving', rating: 4 },
    { pt: 'Aprendizado Contínuo', en: 'Continuous Learning', rating: 5 }
  ]
};

// Projetos cuidadosamente selecionados para o portfólio profissional
// Estes projetos são curados manualmente para mostrar apenas os trabalhos mais relevantes
export const projects = [
  {
    id: 1,
    title: 'MindStorm',
    description: {
      pt: 'Sistema de soluções criativas para problemas complexos com interface em React e backend Node.js.',
      en: 'Creative solutions system for complex problems with React interface and Node.js backend.'
    },
    fullDescription: {
      pt: 'MindStorm é um projeto ambicioso que combina inteligência artificial com design thinking para resolver problemas complexos de forma criativa. O sistema utiliza algoritmos avançados para gerar soluções inovadoras, permitindo aos usuários explorar diferentes abordagens para desafios específicos. O projeto inclui uma interface intuitiva desenvolvida em React, um backend robusto em Node.js e integração com APIs de machine learning.',
      en: 'MindStorm is an ambitious project that combines artificial intelligence with design thinking to solve complex problems creatively. The system uses advanced algorithms to generate innovative solutions, allowing users to explore different approaches to specific challenges. The project includes an intuitive React interface, a robust Node.js backend, and integration with machine learning APIs.'
    },
    technologies: ['JavaScript', 'React', 'Node.js'],
    features: {
      pt: [
        'Interface intuitiva e responsiva',
        'Algoritmos de IA para geração de soluções',
        'Sistema de brainstorming colaborativo',
        'Análise preditiva de resultados',
        'Integração com APIs de machine learning'
      ],
      en: [
        'Intuitive and responsive interface',
        'AI algorithms for solution generation',
        'Collaborative brainstorming system',
        'Predictive result analysis',
        'Machine learning API integration'
      ]
    },
    challenges: {
      pt: [
        'Implementação de algoritmos de IA complexos',
        'Otimização de performance para grandes datasets',
        'Design de UX para funcionalidades avançadas'
      ],
      en: [
        'Implementation of complex AI algorithms',
        'Performance optimization for large datasets',
        'UX design for advanced functionalities'
      ]
    },
    status: 'public',
    image: 'https://images.unsplash.com/photo-1559028006-448665bd7c7f?w=400&h=200&fit=crop&crop=edges',
    icon: '🧠',
    gradient: 'from-purple-500 to-blue-600',
    links: {
      demo: null,
      github: 'https://github.com/pazzutti/mindstorm'
    },
    updated_at: '2024-10-01',
    duration: '3 meses',
    team: 'Grupo de 6 pessoas'
  },
  {
    id: 2,
    title: 'HealthSync',
    description: {
      pt: 'Plataforma de agendamento médico com Spring Boot e Angular desenvolvida em equipe na PUC Minas.',
      en: 'Medical scheduling platform with Spring Boot and Angular developed in team at PUC Minas.'
    },
    fullDescription: {
      pt: 'HealthSync foi desenvolvido como um projeto interdisciplinar na PUC Minas, visando revolucionar o agendamento de consultas médicas. O sistema permite que pacientes agendem consultas de forma intuitiva, enquanto médicos e clínicas gerenciam suas agendas eficientemente. Inclui notificações automáticas, lembretes por email/SMS, integração com calendários e sistema de avaliações. O projeto foi desenvolvido seguindo metodologias ágeis e boas práticas de engenharia de software.',
      en: 'HealthSync was developed as an interdisciplinary project at PUC Minas, aiming to revolutionize medical appointment scheduling. The system allows patients to schedule appointments intuitively, while doctors and clinics manage their schedules efficiently. It includes automatic notifications, email/SMS reminders, calendar integration, and rating system. The project was developed following agile methodologies and software engineering best practices.'
    },
    technologies: ['MySQL', 'Spring Boot', 'Angular'],
    features: {
      pt: [
        'Sistema de agendamento inteligente',
        'Notificações automáticas por email e SMS',
        'Painel administrativo para médicos',
        'Integração com Google Calendar',
        'Sistema de avaliações e feedback',
        'Histórico médico do paciente'
      ],
      en: [
        'Intelligent scheduling system',
        'Automatic email and SMS notifications',
        'Administrative panel for doctors',
        'Google Calendar integration',
        'Rating and feedback system',
        'Patient medical history'
      ]
    },
    challenges: {
      pt: [
        'Sincronização em tempo real de agendas',
        'Implementação de sistema de notificações',
        'Design de arquitetura escalável',
        'Integração com APIs externas'
      ],
      en: [
        'Real-time schedule synchronization',
        'Notification system implementation',
        'Scalable architecture design',
        'External API integration'
      ]
    },
    status: 'private',
    image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=400&h=200&fit=crop&crop=edges',
    icon: '🏥',
    gradient: 'from-green-500 to-teal-600',
    links: {
      demo: null,
      github: null
    },
    updated_at: '2024-09-15',
    duration: '4 meses',
    team: 'Grupo de 7 pessoas'
  },
  {
    id: 3,
    title: 'Não Esquece',
    description: {
      pt: 'App mobile de produtividade com React Native e Firebase para tarefas e lembretes inteligentes.',
      en: 'Mobile productivity app with React Native and Firebase for tasks and smart reminders.'
    },
    fullDescription: {
      pt: 'Aplicativo mobile em desenvolvimento focado em produtividade pessoal. O "Não Esquece" utiliza notificações inteligentes e algoritmos de machine learning para sugerir o melhor momento para realizar tarefas. Inclui sistema de categorização, lembretes baseados em localização e sincronização multi-dispositivo.',
      en: 'Mobile app in development focused on personal productivity. "Não Esquece" uses smart notifications and machine learning algorithms to suggest the best time to perform tasks. Includes categorization system, location-based reminders and multi-device synchronization.'
    },
    technologies: ['React Native', 'Firebase', 'TypeScript', 'Expo'],
    features: {
      pt: [
        'Lembretes inteligentes baseados em contexto',
        'Sincronização em tempo real',
        'Interface intuitiva e minimalista',
        'Notificações por localização',
        'Análise de produtividade'
      ],
      en: [
        'Smart context-based reminders',
        'Real-time synchronization',
        'Intuitive and minimalist interface',
        'Location-based notifications',
        'Productivity analysis'
      ]
    },
    status: 'private',
    image: 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=400&h=200&fit=crop&crop=edges',
    icon: '📋',
    gradient: 'from-orange-500 to-red-600',
    links: {
      demo: null,
      github: null
    },
    updated_at: '2024-10-10',
    duration: 'Em desenvolvimento',
    team: 'Individual'
  },
  {
    id: 4,
    title: 'Conversor de Linguagens',
    description: {
      pt: 'Ferramenta para conversão automática de código entre linguagens de programação.',
      en: 'Tool for automatic code conversion between programming languages.'
    },
    technologies: ['JavaScript', 'TypeScript'],
    status: 'private',
    image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=400&h=200&fit=crop&crop=edges',
    icon: '⚡',
    gradient: 'from-indigo-500 to-purple-600',
    links: {
      demo: null,
      github: null
    },
    updated_at: '2024-09-30'
  },
  {
    id: 5,
    title: 'Chat Suporte',
    description: {
      pt: 'Chatbot para atendimento ao cliente com soluções automatizadas de suporte.',
      en: 'Chatbot for customer service with automated support solutions.'
    },
    technologies: ['Node.js'],
    status: 'private',
    image: 'https://images.unsplash.com/photo-1587560699334-cc4ff634909a?w=400&h=200&fit=crop&crop=edges',
    icon: '💬',
    gradient: 'from-cyan-500 to-blue-600',
    links: {
      demo: null,
      github: null
    },
    updated_at: '2024-08-20'
  },
  {
    id: 7,
    title: 'José Eduardo da Silva Advocacia',
    description: {
      pt: 'Website institucional para advocacia em Direito Médico e LGPD com design premium e UX focada em conversão.',
      en: 'Institutional website for Medical Law and LGPD practice with premium design and conversion-focused UX.'
    },
    fullDescription: {
      pt: 'Projeto de concepção, planejamento e design de uma plataforma institucional para um escritório de advocacia especializado em Direito Médico Preventivo e LGPD. O trabalho envolveu engenharia de prompt, definição estratégica de UX/UI, arquitetura de informação, copywriting jurídico institucional e estruturação técnica de um site moderno, responsivo e orientado à conversão, respeitando integralmente as normas éticas da OAB. O diferencial do projeto foi a criação de uma identidade visual fora do padrão jurídico tradicional, combinando elementos de segurança da informação, tecnologia, autoridade e prevenção jurídica, com foco na clareza, confiança e experiência do usuário.',
      en: 'Conception, planning and design project for an institutional platform for a law firm specialized in Preventive Medical Law and LGPD. The work involved prompt engineering, strategic UX/UI definition, information architecture, institutional legal copywriting and technical structuring of a modern, responsive and conversion-oriented website, fully respecting OAB ethical standards. The project\'s differential was the creation of a visual identity outside the traditional legal standard, combining information security, technology, authority and legal prevention elements, focusing on clarity, trust and user experience.'
    },
    technologies: ['UI/UX Design', 'Copywriting Institucional', 'Tailwind', 'Vite'],
    features: {
      pt: [
        'UX focada em storytelling e autoridade',
        'Estrutura de conteúdo orientada à prevenção jurídica',
        'Design minimalista premium',
        'Comunicação clara para público leigo e técnico',
        'Estrutura otimizada para SEO',
        'Interface responsiva para todos os dispositivos',
        'Design fora do padrão jurídico tradicional',
        'Sistema de conversão e CTAs estratégicos'
      ],
      en: [
        'UX focused on storytelling and authority',
        'Content structure oriented to legal prevention',
        'Premium minimalist design',
        'Clear communication for lay and technical audiences',
        'SEO-optimized structure',
        'Responsive interface for all devices',
        'Design outside traditional legal standards',
        'Strategic conversion and CTA system'
      ]
    },
    challenges: {
      pt: [
        'Criar um site institucional moderno e fora do padrão jurídico tradicional',
        'Posicionar o profissional como autoridade em Direito Médico Preventivo e LGPD',
        'Gerar confiança imediata no visitante',
        'Facilitar contato e conversão',
        'Garantir excelente experiência em mobile'
      ],
      en: [
        'Create a modern institutional website outside traditional legal standards',
        'Position the professional as an authority in Preventive Medical Law and LGPD',
        'Generate immediate trust in the visitor',
        'Facilitate contact and conversion',
        'Ensure excellent mobile experience'
      ]
    },
    status: 'public',
    image: '/joseeduardodasilvaadv.png',
    icon: '🛡️',
    gradient: 'from-slate-700 to-blue-900',
    links: {
      demo: 'https://jose-eduardo-adv.vercel.app/',
      github: null
    },
    updated_at: '2025-01-29',
    duration: 'Concluído',
    team: 'Individual',
    category: 'Web Design • Frontend • UI/UX • Prompt Engineering • Projeto Institucional',
    tags: ['Frontend', 'UX Design', 'UI', 'LegalTech', 'LGPD', 'Web Design', 'Prompt Engineering', 'Product Design']
  },
  {
    id: 8,
    title: 'Sistema de Anamnese Inteligente',
    description: {
      pt: 'Copiloto de IA para triagem e prontuários médicos: transforma relatos brutos de consultas em prontuários estruturados em segundos.',
      en: 'AI copilot for triage and medical records: transforms raw consultation notes into structured records in seconds.'
    },
    fullDescription: {
      pt: 'O Sistema de Anamnese Inteligente é uma aplicação web desenvolvida para otimizar o tempo de profissionais de saúde, transformando relatos brutos e informais de consultas médicas em prontuários estruturados e técnicos em questão de segundos. A aplicação atua como um assistente de tomada de decisão, permitindo que o médico foque no paciente enquanto a inteligência artificial cuida da burocracia e sugere caminhos clínicos.',
      en: 'The Intelligent Anamnesis System is a web application developed to optimize healthcare professionals\' time, transforming raw and informal medical consultation notes into structured technical records in seconds. The application acts as a decision-making assistant, allowing the doctor to focus on the patient while artificial intelligence handles the bureaucracy and suggests clinical pathways.'
    },
    technologies: ['Next.js', 'TypeScript', 'Tailwind CSS', 'FastAPI', 'Python', 'Groq', 'Llama 3', 'Supabase'],
    features: {
      pt: [
        'Estruturação Automatizada (NLP): extrai Queixa Principal, Histórico Clínico, Medicamentos em Uso e Alergias de textos livres',
        'Identificação Simplificada: coleta de Nome, Idade, Peso e Altura integrados ao prontuário gerado',
        'Apoio à Decisão Clínica: sugere hipóteses de tratamento, classes de medicamentos, exames e condutas imediatas',
        'Interface Reativa e Limpa: cards modulares para leitura rápida em ambientes de alta pressão',
        'Processamento ultrarrápido via Groq Cloud com modelo Llama 3 Open Source'
      ],
      en: [
        'Automated Structuring (NLP): extracts Chief Complaint, Clinical History, Medications in Use and Allergies from free texts',
        'Simplified Identification: collects Name, Age, Weight and Height integrated into the generated record',
        'Clinical Decision Support: suggests treatment hypotheses, medication classes, exams and immediate conduct',
        'Reactive and Clean Interface: modular cards for fast reading in high-pressure environments',
        'Ultra-fast processing via Groq Cloud with open-source Llama 3 model'
      ]
    },
    challenges: {
      pt: [
        'Arquitetura escalável com custo zero usando Free Tiers de alto desempenho',
        'Integração de NLP via Groq Cloud com modelo Llama 3 para processamento ultrarrápido',
        'Comunicação assíncrona entre frontend Next.js e backend FastAPI',
        'Persistência segura de dados clínicos no Supabase (PostgreSQL)'
      ],
      en: [
        'Scalable architecture at zero cost using high-performance Free Tiers',
        'NLP integration via Groq Cloud with Llama 3 model for ultra-fast processing',
        'Asynchronous communication between Next.js frontend and FastAPI backend',
        'Secure storage of clinical data in Supabase (PostgreSQL)'
      ]
    },
    status: 'public',
    image: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=400&h=200&fit=crop&crop=edges',
    icon: '🩺',
    gradient: 'from-cyan-600 to-blue-700',
    links: {
      demo: null,
      github: null
    },
    updated_at: '2025-05-01',
    duration: 'Março 2025 - Em desenvolvimento',
    team: 'Individual',
    category: 'FullStack • IA • HealthTech • NLP',
    tags: ['Next.js', 'FastAPI', 'Python', 'AI', 'NLP', 'HealthTech', 'Groq', 'Supabase']
  },
  {
    id: 9,
    title: 'Mordomo Pessoal (Jarvis, por enquanto)',
    description: {
      pt: 'Assistente pessoal de IA para Windows, em desenvolvimento contínuo. Conversa por voz (entrada e saída) numa interface de terminal construída com Rich, onde um avatar reage ao estado da conversa, sobre uma arquitetura modular. Integra o GitHub via MCP, ingere dados de saúde da Mi Band 10, mantém um daemon de lembretes rodando em segundo plano e pode ser controlado remotamente pelo celular por um cliente PWA sobre Tailscale. O repositório ainda se chama "mordomo_pessoal" — nome temporário.',
      en: 'Personal AI assistant for Windows, under continuous development. It talks by voice (input and output) in a Rich-based terminal interface where an avatar reacts to the conversation state, on top of a modular architecture. It integrates GitHub through MCP, ingests health data from the Mi Band 10, keeps a reminder daemon running in the background and can be controlled remotely from a phone via a PWA client over Tailscale. The repository is still named "mordomo_pessoal" — a temporary name.'
    },
    technologies: ['Python', 'PowerShell', 'Rich', 'MCP', 'Tailscale', 'PWA'],
    status: 'private',
    image: '/jarvis-escutando.webp',
    // Cada estado do avatar tem cor e expressão próprias — é o principal
    // canal de feedback da interface, já que a interação é por voz.
    screenshots: [
      {
        image: '/jarvis-escutando.webp',
        caption: {
          pt: 'Estado de escuta: olhos abertos e ondas sonoras sinalizam que o microfone está ativo.',
          en: 'Listening state: open eyes and sound waves signal that the microphone is live.'
        }
      },
      {
        image: '/jarvis-falando.webp',
        caption: {
          pt: 'Estado de fala: a nota musical ao lado do avatar indica saída de áudio em andamento.',
          en: 'Speaking state: the musical note beside the avatar marks audio output in progress.'
        }
      },
      {
        image: '/jarvis-pensando.webp',
        caption: {
          pt: 'Processamento: expressão neutra e olhos estreitados enquanto a resposta é gerada.',
          en: 'Processing: neutral expression and narrowed eyes while the response is generated.'
        }
      },
      {
        image: '/jarvis-tirando-onda.webp',
        caption: {
          pt: 'Registro descontraído: piscadela e sorriso torto acompanham respostas mais informais.',
          en: 'Playful register: a wink and a crooked smile accompany more informal replies.'
        }
      },
      {
        image: '/jarvis-resmungando.webp',
        caption: {
          pt: 'Humor irritadiço: sobrancelhas franzidas e boca virada para baixo dão personalidade ao assistente.',
          en: 'Grumpy mood: furrowed brows and a downturned mouth give the assistant personality.'
        }
      },
      {
        image: '/jarvis-erro.webp',
        caption: {
          pt: 'Estado de erro com identidade visual própria: olhos em X e alerta em vermelho.',
          en: 'Error state with its own visual identity: X-shaped eyes and a red alert banner.'
        }
      }
    ],
    icon: '🤖',
    gradient: 'from-sky-600 to-indigo-800',
    links: {
      demo: null,
      github: null
    },
    updated_at: '2026-07-27',
    duration: {
      pt: 'Desde Julho de 2026 — em desenvolvimento',
      en: 'Since July 2026 — in development'
    },
    team: 'Solo'
  },
  {
    id: 10,
    title: 'Forja Taverna Medieval',
    description: {
      pt: 'E-commerce de temática medieval em desenvolvimento como projeto freelance. Frontend em React com TypeScript e Vite, backend em NestJS com PostgreSQL e Prisma, imagens hospedadas no Cloudinary e pagamentos processados pelo Mercado Pago. Backend publicado no Railway e frontend na Vercel.',
      en: 'Medieval-themed e-commerce in development as a freelance project. React frontend with TypeScript and Vite, NestJS backend with PostgreSQL and Prisma, images hosted on Cloudinary and payments processed through Mercado Pago. Backend deployed on Railway and frontend on Vercel.'
    },
    technologies: ['React', 'TypeScript', 'Vite', 'NestJS', 'PostgreSQL', 'Prisma', 'Cloudinary', 'Mercado Pago'],
    status: 'private',
    icon: '⚔️',
    gradient: 'from-amber-600 to-red-800',
    links: {
      demo: null,
      github: null
    },
    updated_at: '2026-07-27',
    duration: {
      pt: 'Desde Junho de 2026 — em desenvolvimento',
      en: 'Since June 2026 — in development'
    },
    team: 'Solo'
  }
];

export const experience = [
  {
    id: 1,
    company: 'Atalaia/Fictor Alimentos',
    position: {
      pt: 'Estagiário de Desenvolvimento',
      en: 'Development Intern'
    },
    duration: {
      pt: 'Jul 2025 - Outubro 2025',
      en: 'Jul 2025 - October 2025'
    },
    location: 'Belo Horizonte, MG',
    description: {
      pt: [
        'Desenvolvimento de sistemas internos para otimização de processos empresariais',
        'Participação em projetos interdisciplinares com foco no mercado',
        'Manutenção e melhorias em aplicações existentes',
        'Colaboração com equipe multidisciplinar para soluções inovadoras'
      ],
      en: [
        'Development of internal systems for business process optimization',
        'Participation in interdisciplinary projects focused on the market',
        'Maintenance and improvements of existing applications',
        'Collaboration with multidisciplinary team for innovative solutions'
      ]
    },
    technologies: ['React', 'NestJS', 'Mysql', 'Azure', 'Git'],
    type: 'work',
    current: false
  },
  {
    id: 2,
    company: 'PUC Minas',
    position: {
      pt: 'Estudante de Engenharia de Software',
      en: 'Software Engineering Student'
    },
    duration: {
      pt: `Fev 2024 - Presente (${formatSemester(ACADEMIC_START_DATE, 'pt')})`,
      en: `Feb 2024 - Present (${formatSemester(ACADEMIC_START_DATE, 'en')})`
    },
    location: 'Belo Horizonte, MG',
    description: {
      pt: [
        'Participação em projetos interdisciplinares voltados para o mercado',
        'Desenvolvimento de habilidades em programação e engenharia de software',
        'Trabalho em equipe em projetos acadêmicos complexos',
        'Aprendizado contínuo de novas tecnologias e metodologias',
        'Participação da coordenação de redes sociais do curso de Engenharia de Software'
      ],
      en: [
        'Participation in interdisciplinary projects aimed at the market',
        'Development of programming and software engineering skills',
        'Teamwork on complex academic projects',
        'Continuous learning of new technologies and methodologies',
        'Participation in the social media coordination for the Software Engineering course'
      ]
    },
    technologies: ['Java', 'JavaScript', 'SQL', 'UML'],
    type: 'education',
    current: true
  }
];

export const certifications = [
  {
    id: 1,
    title: 'Engenharia de Prompt',
    institution: {
      pt: 'Andre Iacono (Udemy)',
      en: 'Andre Iacono (Udemy)'
    },
    date: 'Oct 2025',
    description: {
      pt: 'Certificação em Engenharia de Prompt, abordando técnicas avançadas para otimização de interações com modelos de linguagem de IA.',
      en: 'Certification in Prompt Engineering, covering advanced techniques for optimizing interactions with AI language models.'
    },
    skills: ['Prompt Engineering', 'AI/ML', 'GPT Models', 'Language Models'],
    image: '/CertificadoEngPrompt.webp',
    category: 'AI/ML',
    credentialId: null,
    verificationUrl: null,
    featured: true
  },
  {
    id: 2,
    title: 'NestJS Sem Enrolação para Iniciantes',
    institution: {
      pt: 'Escola de Devs Oficial (Udemy)',
      en: 'Escola de Devs Oficial (Udemy)'
    },
    date: 'Nov 2025',
    description: {
      pt: 'Curso completo de NestJS para iniciantes, cobrindo os fundamentos do framework e desenvolvimento de APIs robustas.',
      en: 'Complete NestJS course for beginners, covering framework fundamentals and robust API development.'
    },
    skills: ['NestJS', 'Node.js', 'TypeScript', 'API Development', 'Backend'],
    image: '/certificado-nestjs-iniciantes.webp',
    category: 'Backend',
    credentialId: null,
    verificationUrl: null,
    featured: true
  },
  {
    id: 3,
    title: 'BHack Conference',
    institution: {
      pt: 'BHack - Segurança TI Conhecimento',
      en: 'BHack - IT Security Knowledge'
    },
    date: 'Dec 2025',
    description: {
      pt: 'Participação em evento intensivo de cibersegurança com 18 horas de carga horária, abordando segurança ofensiva e defensiva, análise de vulnerabilidades, exploração de sistemas, engenharia social, desenvolvimento seguro (AppSec/DevSec), privacidade, criptografia e tendências atuais em segurança da informação.',
      en: 'Participation in an intensive cybersecurity event with 18 hours of training, covering offensive and defensive security, vulnerability analysis, system exploitation, social engineering, secure development (AppSec/DevSec), privacy, cryptography, and current trends in information security.'
    },
    skills: ['Cybersecurity', 'Penetration Testing', 'Vulnerability Analysis', 'AppSec', 'DevSec', 'Cryptography', 'Social Engineering', 'Incident Response'],
    image: '/certificado-bhack-conference.webp',
    category: 'Security',
    credentialId: null,
    verificationUrl: null,
    featured: true
  },
  {
    id: 4,
    title: 'Pré-Mestrado Internacional em Inteligência Artificial',
    institution: {
      pt: 'Faculdade HUB',
      en: 'Faculdade HUB'
    },
    date: 'Nov 2025',
    description: {
      pt: 'Participação no Pré-Mestrado Internacional em Inteligência Artificial ministrado por Magno Maciel. Programa de 5 horas realizado nos dias 03, 04 e 05 de novembro de 2025, abordando fundamentos de IA, tendências tecnológicas e inovações, aplicações práticas e casos reais, e perspectivas profissionais e acadêmicas para o futuro da inteligência artificial.',
      en: 'Participation in the International Pre-Master\'s in Artificial Intelligence taught by Magno Maciel. 5-hour program held on November 03, 04, and 05, 2025, covering AI fundamentals, technological trends and innovations, practical applications and real cases, and professional and academic perspectives for the future of artificial intelligence.'
    },
    skills: ['Artificial Intelligence', 'Machine Learning', 'AI Applications', 'Tech Trends', 'Innovation', 'Deep Learning'],
    image: '/certificado-pre-mestrado-ia-hub.webp',
    category: 'AI/ML',
    credentialId: null,
    verificationUrl: null,
    featured: true
  }
];

export const socialLinks = [
  {
    name: 'Email',
    url: 'arthur.pazzutti0@gmail.com',
    icon: 'Mail',
    color: '#EA4335'
  },
  {
    name: 'LinkedIn',
    url: 'https://www.linkedin.com/in/arthur-pazzutti-635ba4330/',
    icon: 'Linkedin',
    color: '#0077B5'
  },
  {
    name: 'GitHub',
    url: 'https://github.com/pazzutti',
    icon: 'Github',
    color: '#333'
  },
  {
    name: 'Instagram',
    url: 'https://www.instagram.com/arthur.pazzutti',
    icon: 'Instagram',
    color: '#E4405F'
  }
];